import { type BondRequestExtractionId, type SuretyQuoteId } from "@inrev/common";
import { Option } from "@inrev/core";
import { useFetchAdminBondRequestExtraction } from "@portal/api/bond-request/admin";
import { type FC, useState } from "react";
import { cn } from "../../lib/utils";
import { FloatingPanel, panelScrollRegionProps } from "../../patterns/floating-panel";
import { PDFViewer, type PDFViewerControl } from "../../patterns/pdf-viewer";
import { Button, Icon, Spinner, Text } from "../../primitives";
import {
	BondRequestExtractionSummaryContent,
	formatBondRequestDocumentType,
	formatBondRequestExtractionScope,
} from "./BondRequestExtractionSummaryContent";

const FIELDS_WIDTH = 520;
const PREVIEW_WIDTH = 620;

type BondRequestExtractionSummaryPanelProps = {
	extractionId: BondRequestExtractionId;
	quoteId: SuretyQuoteId;
	fileName?: string;
	/** Enables the side-by-side document preview. Omit for files that can't be embedded. */
	documentPreview?: PDFViewerControl;
	onClose: () => void;
};

export const BondRequestExtractionSummaryPanel: FC<BondRequestExtractionSummaryPanelProps> = ({
	extractionId,
	quoteId,
	fileName,
	documentPreview,
	onClose,
}) => {
	const { data, isLoading, isError } = useFetchAdminBondRequestExtraction(extractionId, quoteId);
	const [isPreviewOpen, setIsPreviewOpen] = useState(false);
	const [previewMounted, setPreviewMounted] = useState(false);

	const showPreview = isPreviewOpen && documentPreview != null;
	const documentTypeLabel = formatBondRequestDocumentType(data?.documentType);
	const scopeLabel = formatBondRequestExtractionScope(data?.scope);
	const subtitleParts = Option.filterNone(
		[fileName, documentTypeLabel, scopeLabel].map((part) =>
			Option.flatMap(part, (value) => (value === "" ? undefined : value)),
		),
	);

	return (
		<FloatingPanel
			width={showPreview ? FIELDS_WIDTH + PREVIEW_WIDTH : FIELDS_WIDTH}
			// The document opens to the left of the fields, so holding the right edge keeps them in place.
			resizeAnchor="right"
			className={showPreview ? "h-[80vh]" : undefined}
			onClose={onClose}
			closeTrackingId="bond-request-extraction-summary-close"
			testId="bond-request-extraction-summary-panel"
		>
			<FloatingPanel.Header>
				<div className="min-w-0 flex-1">
					<FloatingPanel.Title>Extraction Summary</FloatingPanel.Title>
					{subtitleParts.length > 0 && (
						<FloatingPanel.Subtitle>{subtitleParts.join(" · ")}</FloatingPanel.Subtitle>
					)}
				</div>
				<FloatingPanel.Actions>
					{documentPreview != null && (
						<Button
							variant="ghost"
							size="sm"
							className="px-2 text-[12px] font-medium text-secondary hover:text-primary"
							leadingIcon={
								<Icon type={showPreview ? "eye-slash" : "eye"} size="small" color="secondary" />
							}
							onClick={() => {
								setIsPreviewOpen((open) => {
									const next = !open;
									if (next) setPreviewMounted(true);
									return next;
								});
							}}
							data-testid="bond-request-extraction-toggle-preview"
							trackingId="bond-request-extraction-summary-toggle-preview"
						>
							{showPreview ? "Hide Document" : "Show Document"}
						</Button>
					)}
				</FloatingPanel.Actions>
			</FloatingPanel.Header>
			<FloatingPanel.Body>
				{documentPreview != null && previewMounted && (
					<div
						className={cn(
							"flex min-h-0 shrink-0 border-r border-bc-default bg-surface-neutral",
							!showPreview && "hidden",
						)}
						style={{ width: PREVIEW_WIDTH }}
						data-testid="bond-request-extraction-document-preview"
					>
						<PDFViewer
							control={documentPreview}
							className="h-full w-full"
							options={{ fitToWidth: true }}
							trackingId="bond-request-extraction-viewer"
						/>
					</div>
				)}
				<div
					{...panelScrollRegionProps}
					// No block-start padding — sticky DefinitionList headers need a flush scrollport top.
					className="scrollbar-slim scrollbar-gutter-stable min-h-0 min-w-0 flex-1 overflow-y-auto overscroll-contain px-5 pb-5"
				>
					<div className="pt-3">
						{isLoading && (
							<div
								className="flex items-center justify-center py-10"
								data-testid="bond-request-extraction-summary-loading"
							>
								<Spinner size="md" />
							</div>
						)}
						{!isLoading && isError && (
							<Text type="body" color="danger" data-testid="bond-request-extraction-summary-error">
								Unable to load extraction details.
							</Text>
						)}
						{!isLoading && !isError && (
							<BondRequestExtractionSummaryContent output={data?.output ?? {}} />
						)}
					</div>
				</div>
			</FloatingPanel.Body>
		</FloatingPanel>
	);
};
